import React from "react";
import moment from "moment";
import { isEmpty, isNil, map } from "lodash";
import { Button } from "antd";
import { ColumnType } from "antd/lib/table";
import { DATETIME_FORMAT_WITH_MINUTE, EMPTY } from "constants/common";
import { AiccFeedbackRecord, AiccFeedbackType } from "types/model/aiccCustomer";
import { POST_ACTION_COLOR } from "./constants";
import { formatOrdinal } from "./formatOrdinal";
import styles from "./UserFeedbackList.module.scss";

const FEEDBACK_TYPE_LABEL: Record<AiccFeedbackType, string> = {
  positive: "👍 Up",
  negative: "👎 Down",
};

const renderPostAction = ({ postAction, downvoteTurn, endedAtRound }: AiccFeedbackRecord) => {
  if (isNil(postAction)) {
    return EMPTY;
  }

  const roundsAfterDownvote = isNil(downvoteTurn) || isNil(endedAtRound) ? undefined : endedAtRound - downvoteTurn;
  const text = postAction === "rage_quit" ? "End after downvote" : `End after ${roundsAfterDownvote ?? EMPTY} rounds`;

  return (
    <span className={styles.postActionLabel}>
      <i className={styles.postActionDot} style={{ backgroundColor: POST_ACTION_COLOR[postAction] }} />
      {text}
    </span>
  );
};

const getColumns = (onViewConversation: (record: AiccFeedbackRecord) => void): ColumnType<AiccFeedbackRecord>[] => [
  {
    title: "Time",
    dataIndex: "time",
    key: "time",
    width: 160,
    ellipsis: true,
    render: (_, { time }) => moment(time).format(DATETIME_FORMAT_WITH_MINUTE),
  },
  {
    title: "Language",
    dataIndex: "language",
    key: "language",
    width: 120,
    ellipsis: true,
    render: (_, { language }) => language.name || EMPTY,
  },
  {
    title: "Type",
    dataIndex: "type",
    key: "type",
    width: 100,
    ellipsis: true,
    render: (_, { type }) => FEEDBACK_TYPE_LABEL[type],
  },
  {
    title: "Downvote At",
    dataIndex: "downvoteTurn",
    key: "downvoteTurn",
    width: 160,
    ellipsis: true,
    render: (_, { downvoteTurn }) => (isNil(downvoteTurn) ? EMPTY : `${formatOrdinal(downvoteTurn)} round`),
  },
  {
    title: "Post-Action",
    dataIndex: "postAction",
    key: "postAction",
    width: 160,
    ellipsis: true,
    render: (_, record) => renderPostAction(record),
  },
  {
    title: "Trigger Context",
    dataIndex: "triggerContext",
    key: "triggerContext",
    width: 280,
    render: (_, { triggerContext }) => triggerContext || EMPTY,
  },
  {
    title: "Fluency Level",
    dataIndex: "userFluencyLevelSnapshot",
    key: "userFluencyLevelSnapshot",
    width: 130,
    ellipsis: true,
    render: (_, { userFluencyLevelSnapshot }) => userFluencyLevelSnapshot ?? EMPTY,
  },
  {
    title: "Customer Email",
    dataIndex: "customerEmail",
    key: "customerEmail",
    width: 220,
    ellipsis: true,
    render: (_, { customerEmail }) => customerEmail || EMPTY,
  },
  {
    title: "User Input Description",
    dataIndex: "userInputDescription",
    key: "userInputDescription",
    width: 240,
    className: styles.descriptionCell,
    render: (_, { userInputDescription }) =>
      isEmpty(userInputDescription)
        ? EMPTY
        : map(userInputDescription, ({ id, url, text }) => (
            <div key={url} className={styles.descriptionItem}>
              <a
                href={url}
                className={styles.descriptionLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(event) => event.stopPropagation()}
              >
                #{id}
              </a>
              {text ? ` ${text}` : ""}
            </div>
          )),
  },
  {
    title: "Action",
    dataIndex: "action",
    key: "action",
    width: 120,
    render: (_, record) => (
      <Button
        type="link"
        className={styles.viewButton}
        onClick={(event) => {
          event.stopPropagation();
          onViewConversation(record);
        }}
      >
        View Details
      </Button>
    ),
  },
];

export default getColumns;
